// v2 gazelle — a Thomson's-gazelle build: pin-slim legs with dark hooves, a
// sleek tan body carrying the signature dark flank band over a white belly, a
// white rump patch with a black wisp of tail, a graceful neck, a fine head with
// a pale muzzle and flared ears — and the lyre-shaped two-segment horns sweeping
// back then hooking up. Two coats: warm tan and pale sand. Ported 1:1 from
// age-of-mcp's v2/gazelle.ts, using the library's shared `critter` (quadLegs)
// and `safari` (eyes, tube) helpers.
import * as THREE from "three";
import { defineModel, critter, safari, type Part } from "../../lib";

const COATS = [
  { coat: "#cfa45f", head: "#d6ad6a", belly: "#f1e8d2", band: "#4a3826",
    horn: "#3a2e22", hoof: "#2e2620" },
  { coat: "#dbb573", head: "#e2bd7e", belly: "#f6efdc", band: "#54402c",
    horn: "#443628", hoof: "#362c24" },
];

function gazelleParts(v: number): Part[] {
  const c = COATS[v % COATS.length]!;
  const parts: Part[] = critter.quadLegs({
    dx: 0.052, dz: 0.026, bodyY: 0.125, rTop: 0.006, rFoot: 0.0045,
    color: c.coat, hoof: { color: c.hoof, r: 0.005, h: 0.009 },
  });
  parts.push(
    // sleek body, white belly, the dark flank band on each side
    { geometry: new THREE.IcosahedronGeometry(0.043, 0), color: c.coat,
      position: [0, 0.125, 0], scale: [1.65, 0.95, 0.82] },
    { geometry: new THREE.IcosahedronGeometry(0.033, 0), color: c.belly,
      position: [0.004, 0.107, 0], scale: [1.5, 0.72, 0.84] },
    { geometry: new THREE.BoxGeometry(0.095, 0.015, 0.005), color: c.band,
      position: [0.002, 0.118, 0.0355] },
    { geometry: new THREE.BoxGeometry(0.095, 0.015, 0.005), color: c.band,
      position: [0.002, 0.118, -0.0355] },
    { geometry: new THREE.IcosahedronGeometry(0.029, 0), color: c.coat,
      position: [0.062, 0.128, 0] },                                 // chest
    // white rump patch + black wisp of tail
    { geometry: new THREE.IcosahedronGeometry(0.0185, 0), color: c.belly,
      position: [-0.0705, 0.131, 0], scale: [0.55, 0.95, 1.0] },
    safari.tube([-0.073, 0.131, 0], [-0.083, 0.098, 0], 0.0028, 0.0018, c.band),
    // graceful neck and fine head with a pale muzzle
    safari.tube([0.072, 0.148, 0], [0.103, 0.2, 0], 0.016, 0.0125, c.coat, 5),
    { geometry: new THREE.IcosahedronGeometry(0.0195, 0), color: c.head,
      position: [0.115, 0.21, 0], scale: [1.3, 0.95, 0.85] },
    safari.tube([0.132, 0.206, 0], [0.153, 0.2, 0], 0.009, 0.0055, c.belly),
    { geometry: new THREE.IcosahedronGeometry(0.004, 0), color: c.hoof,
      position: [0.156, 0.199, 0] },                                 // nose
    // flared ears
    { geometry: new THREE.ConeGeometry(0.0065, 0.019, 4), color: c.head,
      position: [0.104, 0.225, 0.0135], rotation: [0.55, 0, 0.15] },
    { geometry: new THREE.ConeGeometry(0.0065, 0.019, 4), color: c.head,
      position: [0.104, 0.225, -0.0135], rotation: [-0.55, 0, 0.15] },
  );
  // the lyre horns — each two segments: back-and-out, then hooking up-and-in
  for (const s of [1, -1] as const) {
    parts.push(
      safari.tube([0.111, 0.226, s * 0.007], [0.099, 0.252, s * 0.0125],
        0.0035, 0.0027, c.horn),
      safari.tube([0.099, 0.252, s * 0.0125], [0.107, 0.266, s * 0.009],
        0.0027, 0.0016, c.horn),
    );
  }
  parts.push(...safari.eyes(0.122, 0.2165, 0.011, 0.0035, "#1c150e"));
  return parts;
}

export const gazelleV2 = defineModel({
  id: "gazelle-v2",
  batchId: "fable",
  name: "Gazelle (v2)",
  tags: ["creature","gazelle","low-poly","age-of-mcp","fable"],
  summary: "Low-poly gazelle.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.12,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => gazelleParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => gazelleParts(1) },
  ],
  animations: [],
});
